import BussolaLoader from "@/components/BussolaLoader";
import { useSeo } from "@/hooks/useSeo";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Album, BadgeCheck, ChevronDown, Gamepad2, Handshake, Languages, LayoutGrid, Rocket, Search, ShoppingBag } from "lucide-react";
import { api, fmt, ACCESSORY_CATEGORIES, TCG_CATEGORIES } from "@/lib/api";
import { useAutoRefresh } from "@/lib/live";
import { useGames } from "@/lib/useGames";
import { useCart } from "@/context/CartContext";
import ProductCard from "@/components/ProductCard";

const MACROS = [
  { id: "tutti", label: "Tutti", icon: LayoutGrid },
  { id: "gradate", label: "Gradate", icon: BadgeCheck },
  { id: "tcg", label: "Trading Card Game", icon: Gamepad2 },
  { id: "preordini", label: "Preordini", icon: Rocket },
  { id: "accessori", label: "Accessori", icon: Album },
  { id: "contovendita", label: "Conto Vendita", icon: Handshake },
];

const subTestid = (s) => s.toLowerCase().replace(/[^a-z0-9]/g, "-");

export default function Catalogo() {
  useSeo({ title: "Catalogo: carte singole, sealed e accessori TCG | WANTED", description: "Carte Pokémon, Magic, One Piece e Yu-Gi-Oh! con prezzi di mercato aggiornati: singole, box sigillati, graded e accessori. Acquista online e ritira a Torino.", path: "/catalogo" });
  const games = useGames();
  const { add } = useCart();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  // La pagina vive nell'URL: filtri + pagina sono nell'indirizzo, così il tasto indietro del browser
  // (o il pulsante "torna") riporta esattamente alla vista di prima (es. le gradate con i filtri attivi)
  const [searchParams, setSearchParams] = useSearchParams();
  const MACRO_IDS = MACROS.map((m) => m.id);
  const serialize = (o) => {
    const sp = new URLSearchParams();
    if (o.macro && o.macro !== "tutti") sp.set("macro", o.macro);
    if (o.game && o.game !== "Tutti") sp.set("gioco", o.game);
    if (o.lang) sp.set("lingua", o.lang);
    if (o.cat && o.cat !== "Tutte") sp.set("categoria", o.cat);
    if (o.grade && o.grade !== "Tutti") sp.set("grado", o.grade);
    if (o.q) sp.set("cerca", o.q);
    if (o.tag) sp.set("tag", o.tag);
    if (o.sort) sp.set("ordina", o.sort);
    if (o.page > 1) sp.set("pagina", String(o.page));
    return sp.toString();
  };
  const parseUrl = (sp) => {
    const m = sp.get("macro");
    return {
      macro: MACRO_IDS.includes(m) ? m : "tutti",
      game: sp.get("gioco") || "Tutti",
      lang: sp.get("lingua") || null,
      cat: sp.get("categoria") || "Tutte",
      grade: sp.get("grado") || "Tutti",
      q: sp.get("cerca") || "",
      tag: sp.get("tag") || "",
      sort: sp.get("ordina") || "",
      page: Math.max(1, parseInt(sp.get("pagina"), 10) || 1),
    };
  };
  const initial = parseUrl(searchParams);
  const [page, setPage] = useState(initial.page);
  const [macro, setMacro] = useState(initial.macro);
  const [game, setGame] = useState(initial.game);
  const [lang, setLang] = useState(initial.lang);
  const [cat, setCat] = useState(initial.cat);
  const [grade, setGrade] = useState(initial.grade);
  const [facets, setFacets] = useState({ categories: {}, languages: {}, tags: {}, gradings: {}, grades: {}, graded_total: 0 });
  const [qInput, setQInput] = useState(initial.q);
  const [q, setQ] = useState(initial.q);
  const [tag, setTag] = useState(initial.tag);
  const [sort, setSort] = useState(initial.sort);
  const PAGE_SIZE = 24;
  const [vetrina, setVetrina] = useState([]);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  // Numeri di pagina: tutti se <=7, altrimenti finestra attorno alla corrente con puntini
  const pageNums = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const nums = [1];
    const lo = Math.max(2, page - 1), hi = Math.min(totalPages - 1, page + 1);
    if (lo > 2) nums.push("…");
    for (let n = lo; n <= hi; n++) nums.push(n);
    if (hi < totalPages - 1) nums.push("…");
    nums.push(totalPages);
    return nums;
  };
  const goPage = (n) => {
    setPage(n);
    document.querySelector('[data-testid="catalog-grid"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    api.get("/consignment/vetrina").then((r) => setVetrina(r.data)).catch(() => setVetrina([]));
  }, []);

  const pickMacro = (m) => {
    if (macro === m) {
      setMacro("tutti");
    } else {
      setMacro(m);
    }
    setGame("Tutti");
    setLang(null);
    setCat("Tutte");
    setGrade("Tutti");
    setTag("");
  };

  const pickGame = (g) => {
    setGame(g);
    setLang(null);
    setCat("Tutte");
    setTag("");
  };

  useEffect(() => {
    if (macro === "contovendita") return;
    const params = {};
    params.macro = macro; // anche "tutti" esplicito: il backend esclude i preordini
    if ((macro === "tcg" || macro === "preordini") && game !== "Tutti") params.game = game;
    if (macro === "tcg" && lang && lang !== "Tutte") params.lang = lang;
    if (macro === "gradate" && cat !== "Tutte") params.grading = cat;
    api.get("/products/facets", { params }).then((r) => setFacets(r.data)).catch(() => setFacets({ categories: {}, languages: {}, tags: {}, gradings: {}, grades: {}, graded_total: 0 }));
  }, [macro, game, lang, cat]);

  // Debounce ricerca: una sola chiamata API dopo 300ms di pausa, non una per tasto
  useEffect(() => {
    const t = setTimeout(() => setQ(qInput.trim()), 300);
    return () => clearTimeout(t);
  }, [qInput]);

  const buildParams = useCallback((skip = 0) => {
    const params = { skip, limit: PAGE_SIZE };
    params.macro = macro; // anche "tutti" esplicito: i preordini restano solo nella loro macro
    if (macro === "tcg") {
      if (game !== "Tutti") params.game = game;
      if (lang && lang !== "Tutte") params.lang = lang;
      if (cat !== "Tutte") params.category = cat;
    }
    if (macro === "preordini" && game !== "Tutti") params.game = game;
    if (macro === "accessori" && cat !== "Tutte") params.category = cat;
    if (macro === "gradate" && cat !== "Tutte") params.grading = cat;
    if (macro === "gradate" && grade !== "Tutti") params.grade = grade;
    if (q) params.q = q;
    if (tag) params.tag = tag;
    if (sort) params.sort = sort;
    return params;
  }, [macro, game, lang, cat, grade, q, tag, sort]);

  // Cambio filtri → si riparte sempre dalla prima pagina. Confronto sulla firma dei filtri (non ref "primo run"):
  // StrictMode esegue gli effetti due volte in dev e un ref-guard azzererebbe la pagina anche all'apertura di un link condiviso
  const filtersSig = [macro, game, lang, cat, grade, q, tag, sort].join("|");
  const prevFiltersSig = useRef(filtersSig);
  const restoringFromUrl = useRef(false); // tasto indietro: la pagina arriva dall'URL, non si azzera
  useEffect(() => {
    if (prevFiltersSig.current === filtersSig) return;
    prevFiltersSig.current = filtersSig;
    if (restoringFromUrl.current) { restoringFromUrl.current = false; return; }
    setPage(1);
  }, [filtersSig]);

  // Stato → URL: ogni cambio filtri/pagina diventa un indirizzo condivisibile (e una voce di cronologia)
  const stateStr = serialize({ macro, game, lang, cat, grade, q, tag, sort, page });
  const lastSynced = useRef(stateStr);
  useEffect(() => {
    if (lastSynced.current === stateStr) return;
    lastSynced.current = stateStr;
    setSearchParams(stateStr ? new URLSearchParams(stateStr) : {});
  }, [stateStr]);
  // URL → stato: tasto indietro/avanti del browser ripristina esattamente filtri e pagina
  useEffect(() => {
    const urlStr = searchParams.toString();
    if (urlStr === lastSynced.current) return;
    lastSynced.current = urlStr;
    restoringFromUrl.current = true;
    const v = parseUrl(searchParams);
    setMacro(v.macro); setGame(v.game); setLang(v.lang); setCat(v.cat); setGrade(v.grade);
    setQInput(v.q); setQ(v.q); setTag(v.tag); setSort(v.sort); setPage(v.page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  // Se il totale si riduce (es. vendite), la pagina corrente rientra nel range.
  // Il clamp scatta solo DOPO il primo caricamento (total>0): all'apertura di un link condiviso (?pagina=2) il totale è ancora 0
  useEffect(() => { if (total > 0 && page > totalPages) { setPage(totalPages); } }, [total, page, totalPages]);

  useEffect(() => {
    if (macro === "contovendita") { setProducts([]); setTotal(0); setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    api.get("/products", { params: buildParams((page - 1) * PAGE_SIZE) })
      .then((r) => {
        if (cancelled) return;
        setProducts(r.data);
        setTotal(parseInt(r.headers["x-total-count"] || String(r.data.length), 10));
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [buildParams, page]);

  // Refresh silenzioso: prezzi, stock e novità della pagina corrente si aggiornano da soli
  const silentRefresh = useCallback(() => {
    if (macro === "contovendita") return;
    api.get("/products", { params: buildParams((page - 1) * PAGE_SIZE) })
      .then((r) => {
        setProducts(r.data);
        setTotal(parseInt(r.headers["x-total-count"] || String(r.data.length), 10));
      })
      .catch(() => {});
    const fParams = {};
    fParams.macro = macro; // anche "tutti" esplicito
    if ((macro === "tcg" || macro === "preordini") && game !== "Tutti") fParams.game = game;
    if (macro === "tcg" && lang && lang !== "Tutte") fParams.lang = lang;
    api.get("/products/facets", { params: fParams }).then((r) => setFacets(r.data)).catch(() => {});
  }, [buildParams, page, macro, game, lang]);
  useAutoRefresh(silentRefresh, 60000);
  // Anche la vetrina conto vendita si aggiorna da sola (pezzi venduti/nuovi)
  useAutoRefresh(useCallback(() => {
    api.get("/consignment/vetrina").then((r) => setVetrina(r.data)).catch(() => {});
  }, []), 60000);

  const chip = (active) =>
    `text-xs font-mono uppercase tracking-wider px-3.5 py-1.5 rounded border transition-all active:scale-[0.97] ${
      active ? "border-red-600/60 text-red-400 bg-red-600/10" : "border-white/10 text-zinc-500 hover:text-white hover:border-red-600/40"
    }`;

  const subRow = (key, label, options, value, onPick, testidPrefix, counts) => (
    <motion.div
      key={key}
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
      className="overflow-hidden"
    >
      <div className="flex flex-wrap items-center gap-2 py-1" data-testid={`sub-filters-${key}`}>
        <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-600 mr-1">{label}</span>
        {options.map((s) => {
          const isAll = s === "Tutti" || s === "Tutte";
          const count = counts ? counts[s] ?? 0 : null;
          const disabled = !isAll && counts !== null && count === 0;
          return (
            <button
              key={s}
              data-testid={`${testidPrefix}-${subTestid(s)}`}
              onClick={() => !disabled && onPick(s)}
              disabled={disabled}
              title={disabled ? "Nessun articolo disponibile" : undefined}
              className={disabled
                ? "text-xs font-mono uppercase tracking-wider px-3.5 py-1.5 rounded border border-white/5 text-zinc-700 cursor-not-allowed line-through"
                : chip(value === s)}
            >
              {s}
              {counts && !isAll && count > 0 && <span className="ml-1.5 text-[9px] text-zinc-500">({count})</span>}
            </button>
          );
        })}
      </div>
    </motion.div>
  );

  return (
    <main data-testid="catalog-page" className="max-w-7xl mx-auto px-4 sm:px-8 pt-32 pb-24 min-h-screen">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
        <p className="text-xs font-mono uppercase tracking-[0.25em] text-red-500 font-semibold mb-3">Wanted inventory</p>
        <h1 className="text-4xl sm:text-5xl font-display font-black uppercase tracking-tighter text-white">
          Catalogo
        </h1>
        <p className="text-sm text-zinc-500 mt-3 font-mono" data-testid="catalog-count">
          {macro === "contovendita" ? `${vetrina.filter((i) => !q || (i.name + " " + (i.detail || "")).toLowerCase().includes(q.toLowerCase())).length} pezzi unici in vetrina` : `${total} pezzi nel caveau`}
        </p>
      </motion.div>

      <div className="mt-10 space-y-4">
        <div className="flex flex-wrap gap-2" data-testid="macro-filters">
          {MACROS.filter((m) => (m.id !== "gradate" || (facets.graded_total || 0) > 0) && (m.id !== "contovendita" || vetrina.length > 0)).map((m) => (
            <button
              key={m.id}
              data-testid={`filter-macro-${m.id}`}
              onClick={() => pickMacro(m.id)}
              aria-expanded={macro === m.id && m.id !== "tutti"}
              className={`inline-flex items-center gap-2 whitespace-nowrap text-xs font-display font-bold uppercase tracking-wider px-5 py-2.5 rounded-full border transition-all active:scale-[0.97] ${
                macro === m.id ? "bg-red-600 border-red-600 text-white" : "border-white/15 text-zinc-400 hover:border-red-600/50 hover:text-white"
              }`}
            >
              <m.icon size={13} /> {m.label}
              {facets.macro_counts?.[m.id] != null && (
                <span className={`text-[10px] font-mono font-bold shrink-0 ${macro === m.id ? "text-white/80" : "text-zinc-600"}`} data-testid={`filter-macro-count-${m.id}`}>({facets.macro_counts[m.id]})</span>
              )}
              {m.id !== "tutti" && <ChevronDown size={12} className={`transition-transform duration-300 ${macro === m.id ? "rotate-180" : ""}`} />}
            </button>
          ))}
        </div>

        <AnimatePresence initial={false}>
          {(macro === "tcg" || macro === "preordini") && subRow("gioco", "Gioco:", ["Tutti", ...games], game, pickGame, "filter-game", facets.games || null)}
          {macro === "tcg" && game !== "Tutti" && (
            <div key="lang-wrap">
              {subRow("lingua", "Lingua:", ["Tutte", ...Object.keys(facets.languages).sort()], lang || "", (l) => { setLang(l); setCat("Tutte"); }, "filter-lang", facets.languages)}
            </div>
          )}
          {macro === "tcg" && game !== "Tutti" && lang !== null && subRow("categoria", "Categoria:", ["Tutte", ...TCG_CATEGORIES], cat, setCat, "filter-cat", facets.categories)}
          {macro === "accessori" && subRow("accessori", "Tipo:", ["Tutte", ...ACCESSORY_CATEGORIES], cat, setCat, "filter-cat", facets.categories)}
          {macro === "gradate" && subRow("gradate", "Casa di gradazione:", ["Tutte", ...Object.keys(facets.gradings).sort()], cat, (c) => { setCat(c); setGrade("Tutti"); }, "filter-grading", facets.gradings)}
          {macro === "gradate" && cat !== "Tutte" && Object.keys(facets.grades || {}).length > 0 && subRow("voto", "Voto:", ["Tutti", ...Object.keys(facets.grades).sort((a, b) => parseFloat(b) - parseFloat(a))], grade, setGrade, "filter-grade", facets.grades)}
        </AnimatePresence>

        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-56 sm:max-w-xs">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              data-testid="catalog-search-input"
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              placeholder="Cerca carta, box, set..."
              className="w-full bg-[#0D0D0E] border border-white/10 focus:border-red-600/70 rounded-full pl-10 pr-4 py-2.5 text-sm text-white placeholder:text-zinc-600 outline-none transition-colors"
            />
          </div>
          {macro !== "contovendita" && (
            <select
              data-testid="catalog-sort-select"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="bg-[#0D0D0E] border border-white/10 focus:border-red-600/70 rounded-full px-4 py-2.5 text-xs font-mono uppercase tracking-wider text-zinc-300 outline-none transition-colors cursor-pointer"
            >
              <option value="">Rilevanza</option>
              <option value="price_asc">Prezzo crescente</option>
              <option value="price_desc">Prezzo decrescente</option>
              <option value="disponibili">Disponibili prima</option>
              <option value="novita">Novità</option>
            </select>
          )}
        </div>

        {Object.keys(facets.tags || {}).length > 0 && (
          <div className="flex flex-wrap items-center gap-2" data-testid="tag-filters">
            <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-600 mr-1">Tag:</span>
            {Object.entries(facets.tags).map(([t, n]) => (
              <button
                key={t}
                data-testid={`filter-tag-${subTestid(t)}`}
                onClick={() => setTag(tag === t ? "" : t)}
                title={tag === t ? `Rimuovi filtro ${t}` : `Mostra articoli con tag ${t}`}
                className={chip(tag === t)}
              >
                #{t}
                <span className="ml-1.5 text-[9px] text-zinc-500">({n})</span>
              </button>
            ))}
          </div>
        )}

        {macro === "tcg" && game !== "Tutti" && lang === null && (
          <p data-testid="catalog-lang-hint" className="text-xs font-mono text-zinc-600 uppercase tracking-wider flex items-center gap-2">
            <Languages size={13} className="text-red-500" /> Scegli la lingua per vedere le categorie disponibili
          </p>
        )}
      </div>

      {macro === "contovendita" ? (
        (() => {
          const shown = vetrina.filter((i) => !q || (i.name + " " + (i.detail || "")).toLowerCase().includes(q.toLowerCase()));
          return shown.length === 0 ? (
            <p data-testid="vetrina-empty" className="text-zinc-500 font-mono text-sm mt-20 text-center">Nessun pezzo in conto vendita al momento.</p>
          ) : (
            <>
              <p className="text-xs font-mono text-zinc-500 mt-8 max-w-2xl leading-relaxed">
                Pezzi unici affidati a Wanted dai collezionisti: vendita gestita in negozio con conto vendita 80/20. Contattaci o passa in store per bloccarli.
              </p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-6" data-testid="vetrina-grid">
                {shown.map((i, idx) => (
                  <motion.div key={i.id} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: (idx % 12) * 0.05 }}>
                    <Link to={`/vetrina/${i.id}`} data-testid={`vetrina-card-${i.id}`} className="slab-frame rounded-2xl overflow-hidden group block hover:border-red-600/40 transition-colors">
                      <div className="relative aspect-[4/3] bg-[#0D0D0E] border-b border-white/10 overflow-hidden flex items-center justify-center">
                        {i.image ? (
                          <>
                            <img src={i.image} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover blur-2xl opacity-30 scale-110" />
                            <img src={i.image} alt={i.name} className="relative w-full h-full object-contain p-2 group-hover:scale-[1.03] transition-transform duration-700" />
                          </>
                        ) : (
                          <Handshake size={28} className="text-zinc-700" />
                        )}
                      </div>
                      <div className="p-5">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-[9px] font-mono uppercase tracking-widest font-bold px-2.5 py-1 rounded-full border text-amber-400 border-amber-500/30 bg-amber-500/10">Conto vendita</span>
                          <span className="text-[9px] font-mono uppercase tracking-widest text-zinc-600">Pezzo unico</span>
                          {i.condition && <span data-testid={`vetrina-condition-${i.id}`} className="text-[9px] font-mono uppercase tracking-widest font-bold px-2.5 py-1 rounded-full border text-zinc-300 border-white/20">{i.condition}</span>}
                        </div>
                        <h3 className="font-display text-base font-bold uppercase tracking-tight text-white group-hover:text-red-400 transition-colors leading-snug">{i.name}</h3>
                        {i.detail && <p className="text-[11px] font-mono text-zinc-500 mt-1.5">{i.detail}</p>}
                        <div className="flex items-center justify-between mt-3">
                          <p className="font-mono font-bold text-lg text-red-400">{fmt(i.agreed_price)}</p>
                          <button
                            data-testid={`vetrina-add-${i.id}`}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              add({ id: i.id, name: i.name, price: i.agreed_price, final_price: i.agreed_price, image: i.image, is_consignment: true, in_stock: 1 });
                            }}
                            className="inline-flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white text-[10px] font-display font-bold uppercase tracking-wider px-4 py-2 rounded-full transition-all active:scale-[0.97]"
                          >
                            <ShoppingBag size={12} /> Aggiungi
                          </button>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </>
          );
        })()
      ) : loading ? (
        <div className="mt-10"><BussolaLoader testo="Apertura del caveau..." /></div>
      ) : products.length === 0 ? (
        <p data-testid="catalog-empty" className="text-zinc-500 font-mono text-sm mt-20 text-center">Nessun pezzo trovato con questi filtri.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6 mt-10" data-testid="catalog-grid">
            {products.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i % PAGE_SIZE} />
            ))}
          </div>
          {totalPages > 1 && (
            <div className="flex flex-wrap items-center justify-center gap-2 mt-12" data-testid="catalog-pagination">
              <button data-testid="catalog-page-prev" onClick={() => goPage(page - 1)} disabled={page <= 1} title="Pagina precedente" className="p-2.5 rounded-full border border-white/15 text-zinc-400 hover:text-white hover:border-red-600/50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
                <ChevronDown size={14} className="rotate-90" />
              </button>
              {pageNums().map((n, i) => n === "…" ? (
                <span key={`dots-${i}`} className="text-zinc-600 text-xs font-mono px-1">…</span>
              ) : (
                <button key={n} data-testid={`catalog-page-${n}`} onClick={() => goPage(n)} className={`min-w-9 h-9 px-2 rounded-full text-xs font-mono font-bold transition-all active:scale-[0.95] ${n === page ? "bg-red-600 text-white" : "border border-white/15 text-zinc-400 hover:text-white hover:border-red-600/50"}`}>
                  {n}
                </button>
              ))}
              <button data-testid="catalog-page-next" onClick={() => goPage(page + 1)} disabled={page >= totalPages} title="Pagina successiva" className="p-2.5 rounded-full border border-white/15 text-zinc-400 hover:text-white hover:border-red-600/50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
                <ChevronDown size={14} className="-rotate-90" />
              </button>
            </div>
          )}
          <p data-testid="catalog-progress" className="text-zinc-600 font-mono text-xs mt-8 text-center uppercase tracking-wider">
            {total} articoli{totalPages > 1 ? ` — pagina ${page} di ${totalPages}` : ""}
          </p>
        </>
      )}
    </main>
  );
}
